import React, { useState } from "react";
import { useCart } from "../../context/CartContext";
import { toast } from "react-toastify";

const PlanPricing = ({ pricing, service, serviceIndex, onSelection, activeTab, vendor = null }) => {
  // ✅ Hooks always at the top

  // 🔍 DEBUG: See what the parent sent
  console.group("📥 PlanPricing - Props Received");
  console.log("Service Name:", service?.name);
  console.log("Vendor Object:", vendor);
  console.log("Vendor ID from Prop:", vendor?._id || vendor?.vendor_id);
  console.log("Full Pricing Table:", pricing);
  console.groupEnd();

  const [selectedFrequency, setSelectedFrequency] = useState("once"); // for Dog Walking
  const [selectedDuration, setSelectedDuration] = useState("1hr");   // for Day Care
  const [selectedPlan, setSelectedPlan] = useState("one_time");
    // common
  const { addToCart, isInCart, updateQuantity, getCartItemQuantity } = useCart();

  if (!pricing) {
    return <div className="p-3">No pricing available</div>;
  }

  // Plan labels
  const planLabels = {
    one_time: "One Time",
    weekly: "Weekly",
    monthly: "Monthly",
    quaterly: "Quarterly", // spelling same as API
  };

  // Duration labels (Day Care)
  const durationLabels = {
    "1hr": "1 Hour",
    "2hr": "2 Hours",
    "4hr": "4 Hours",
    "8hr": "8 Hours",
  };

  // Detect structure
  const hasFrequency = Object.keys(pricing).some((key) =>
    ["once", "twice"].includes(key)
  ); // Dog Walking

  const hasDuration = Object.keys(pricing).some((key) =>
    ["1hr", "2hr", "4hr", "8hr"].includes(key)
  ); // Day Care

  // Pick correct price
  let currentPrice = 0;
  if (hasFrequency) {
    currentPrice = pricing?.[selectedFrequency]?.[selectedPlan] ?? 0;
  } else if (hasDuration) {
    currentPrice = pricing?.[selectedDuration]?.[selectedPlan] ?? 0;
  } else {
    currentPrice = pricing?.[selectedPlan] ?? 0; // fallback
  }

  // Generate unique service ID for cart
  const uniqueServiceId = `${service?.name}_${serviceIndex}_${activeTab}_${selectedFrequency || selectedDuration}_${selectedPlan}`;

  // Check if item is in cart
  const isInCartCheck = isInCart(uniqueServiceId, activeTab, null, null, null);
  const cartQuantity = getCartItemQuantity(uniqueServiceId, activeTab, null, null, null);

  // Handle add to cart
  // const handleAddToCart = () => {
  //   if (!service) {
  //     toast.error("Service information is missing");
  //     return;
  //   }
  //   if (currentPrice === 0) {
  //     toast.error("Please select a valid pricing option");
  //     return;
  //   }

  //   const cartItem = {
  //     id: uniqueServiceId,
  //     name: service.name,
  //     price: currentPrice,
  //     description: service.description,
  //     duration: service.duration?.flat?.minutes,
  //     pet_type: service.pet_type,
  //     service_type: activeTab,
  //     frequency: selectedFrequency,
  //     duration_option: selectedDuration,
  //     plan: selectedPlan,
  //     vendor_id: vendor?._id || vendor?.id,
  //     pricing_details: {
  //       frequency: selectedFrequency,
  //       duration: selectedDuration,
  //       plan: selectedPlan,
  //       original_pricing: pricing
  //     }
  //   };

  //   addToCart(cartItem);
  //   toast.success(`${service.name} added to cart!`);
  // };
const handleAddToCart = () => {
  if (!service || !service.name) {
    toast.error("Service information is missing");
    return;
  }
  if (currentPrice === 0) {
    toast.error("Please select a valid pricing option");
    return;
  }

  // 1. Setup Normalization for logic checks
  const currentTab = activeTab?.toLowerCase().trim();
  const isDayCare = ["day care", "day care services", "day_care"].includes(currentTab);
  const isDogWalking = ["dog walks", "dog walking", "dog_walking"].includes(currentTab);
  const normalizedType = isDayCare ? "day_care" : isDogWalking ? "dog_walking" : activeTab;

  const pending = {
    // Basic Info
   // id: `${service.name}_${serviceIndex}_${activeTab}`,
    service_id: service._id || service.id, // Fixed: Ensuring service_id exists
    name: service.name,
    price: currentPrice,
    description: service.description,
    duration: service.duration?.flat?.minutes || 0,
    pet_type: service.pet_type,
    service_type: activeTab,
    normalized_type: normalizedType,
    //vendor_id: vendor?._id || vendor?.id,
    vendor_id: vendor?.vendor_id || service?.vendor_id || vendor?._id,
    vendor_name:vendor?.profile?.brand || vendor?.vendor_brand || vendor?.profile?.vendor_brand_name|| vendor?.companyName,

    // 2. ROOT KEYS (For your Console Log and TimeSlot component)
    pricing_duration: isDayCare ? selectedDuration : undefined,
    pricing_frequency: isDayCare ? selectedPlan : (isDogWalking ? selectedFrequency : undefined),
    pricing_plan: isDogWalking ? selectedPlan : undefined,

    // 3. BACKWARD COMPATIBILITY (Keeping your existing keys just in case)
    frequency: selectedFrequency,
    duration_option: selectedDuration,
    plan: selectedPlan,

    pricing_details: {
      frequency: selectedFrequency,
      duration: selectedDuration,
      plan: selectedPlan,
      original_pricing: pricing
    }
  };
   // 🔍 DEBUGGING CHECKPOINT
 
  if (typeof onSelection === "function") {
    onSelection(pending);
  }

  toast.success(`₹${pending.price} is ready for time selection`);
};
  // Handle quantity update
  const handleQuantityUpdate = (newQuantity) => {
    if (newQuantity <= 0) {
      updateQuantity(uniqueServiceId, activeTab, null, null, null, 0);
    } else {
      updateQuantity(uniqueServiceId, activeTab, null, null, null, newQuantity);
    }
  };

  return (
    <div className="p-3 border rounded-lg bg-white shadow-inner">
      
      {/* Frequency Tabs → Dog Walking */}
      {hasFrequency && (
        <div className="flex gap-3 mb-4 flex-wrap">
          <p className="w-full text-left text-sm font-semibold text-[#000048] mb-1">Select Frequency:</p>
          {["once", "twice"].map((freq) => (
            <button
              key={freq}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition ${
                selectedFrequency === freq
                  ? "bg-[#FD5901] text-white shadow-md"
                  : "border border-[#FD5901] text-[#FD5901] bg-white hover:bg-[#ffe6d8]"
              }`}
              onClick={() => setSelectedFrequency(freq)}
            >
              {freq === "once" ? "Once a Day" : "Twice a Day"}
            </button>
          ))}
        </div>
      )}

      {/* Duration Tabs → Day Care */}
      {hasDuration && (
        <div className="flex gap-3 mb-4 flex-wrap">
          <p className="w-full text-left text-sm font-semibold text-[#000048] mb-1">Select Duration:</p>
          {Object.keys(durationLabels).map((duration) => (
            <button
              key={duration}
              className={`px-3 py-2 rounded-lg font-medium text-sm transition ${
                selectedDuration === duration
                  ? "bg-[#FD5901] text-white shadow-md"
                  : "border border-[#FD5901] text-[#FD5901] bg-white hover:bg-[#ffe6d8]"
              }`}
              onClick={() => setSelectedDuration(duration)}
            >
              {durationLabels[duration]}
            </button>
          ))}
        </div>
      )}

      {/* Plan Tabs → Common */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <p className="w-full text-left text-sm font-semibold text-[#000048] mb-1">Select Plan:</p>
        {Object.keys(planLabels).map((planKey) => (
          <button
            key={planKey}
            className={`px-3 py-1 rounded text-sm transition ${
              selectedPlan === planKey
                ? "bg-[#FD5901] text-white shadow-md"
                : "border border-[#FD5901] text-[#FD5901] bg-white hover:bg-[#ffe6d8]"
            }`}
            onClick={() => setSelectedPlan(planKey)}
          >
            {planLabels[planKey]}
          </button>
        ))}
      </div>

      {/* ⭐️ Price Display and Final Add to Cart Button (Only in the modal) ⭐️ */}
      <div className="text-center p-4 border rounded-lg bg-gray-100 shadow">
        <p className="text-lg font-semibold text-[#000048]">
          {hasFrequency &&
            `${selectedFrequency === "once" ? "Once" : "Twice"} a Day - ${
              planLabels[selectedPlan]
            }`}
          {hasDuration &&
            `${durationLabels[selectedDuration]} - ${planLabels[selectedPlan]}`}
          {!hasFrequency && !hasDuration && planLabels[selectedPlan]}
        </p>
        <p className="text-3xl font-bold text-[#FD5901] my-2">₹{currentPrice}</p>
        
        <button
          className="mt-4 w-full bg-[#FD5901] text-white py-3 px-4 rounded-lg hover:bg-[#e05514] transition font-semibold text-lg"
          onClick={handleAddToCart}
          disabled={currentPrice === 0}
        >
          Add to Cart & Pick Time
        </button>
      </div>
    </div>
  );
};

export default PlanPricing;